<template>
  <RouterLink v-slot="{ isActive, href }" custom :to="{ name: route }">
    <a
      class="navigation-item-link"
      :class="{
        'navigation-item-link--active': isActive && !disabled,
        'navigation-item-link--disabled': disabled,
      }"
      :href="disabled ? false : !hasChild && href"
      @click="$emit('click', $event)"
    >
      <div class="navigation-item-link__inner">
        <div v-if="icon" class="flex-container align-center">
          <ElBadge is-dot :hidden="!showBadge">
            <component :is="icon" class="navigation-item-link__icon" />
          </ElBadge>
        </div>

        <div class="navigation-item-link__title flex-child-grow">
          {{ $t(title) }}

          <div v-if="newBadge" class="navigation-item-link__badges">
            <NewBadge />
          </div>

          <i
            v-if="warnBadge"
            class="material-icons navigation-item-link__warn-badge"
          >
            error_outline
          </i>

          <div v-if="hasCountBadge" class="navigation-item-link__count-badge">
            {{ badgeCount }}
          </div>
        </div>

        <i
          v-if="leftIcon"
          class="navigation-item-link__arrow-icon"
          :class="leftIcon"
        />
      </div>
    </a>
  </RouterLink>
</template>

<script>
import NewBadge from '@@/common/NewBadge';

export default {
  name: 'NavigationItemLink',
  components: {
    NewBadge,
  },
  computed: {
    hasCountBadge() {
      return Number(this.badgeCount) > 0;
    },
  },
  props: {
    route: {
      type: String,
      required: true,
    },
    icon: {
      type: Object,
      default: null,
    },
    leftIcon: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      required: true,
    },
    hasChild: {
      type: Boolean,
      default: false,
    },
    showBadge: {
      type: Boolean,
      default: false,
    },
    newBadge: {
      type: Boolean,
      default: false,
    },
    warnBadge: {
      type: Boolean,
      default: false,
    },
    badgeCount: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss">
.navigation-item-link {
  width: 100%;
  height: $lb-side-nav-item-height;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  position: relative;
  padding: $lb-side-nav-padding;
  color: $lb-side-nav-color;
  font-size: $lb-side-nav-font-size;
  font-weight: $lb-side-nav-font-weight;
  outline: none;
  transition: color 0.25s;

  &::before {
    content: '';
    display: block;
    opacity: 0;
    width: rem-calc(4px);
    height: 70%;
    transform: translateY(-50%);
    background-color: $lb-side-nav-active-color;
    border-radius: 0 10px 10px 0;
    position: absolute;
    top: 50%;
    left: 0;

    @include rtl() {
      border-radius: 10px 0 0 10px;
      left: auto;
      right: 0;
    }
  }

  &--active {
    background: linear-gradient(
      -90deg,
      rgba($lb-color-primary, 0.03),
      transparent
    );
    color: $lb-color-primary;

    &::before {
      opacity: 1;
    }
  }

  &__inner {
    display: flex;
    flex: 1 0 auto;
    align-items: center;
    width: 100%;
  }

  &__title {
    min-width: 0;
    max-width: 99%;
    padding: 0 $global-margin;
  }

  &__icon {
    font-size: $lb-side-nav-font-size * 1.25;
    vertical-align: middle;

    path {
      fill: $lb-side-nav-icon-color;
    }
  }

  &__badges {
    display: inline-block;
    margin: $global-margin/3;
  }

  &__warn-badge {
    margin-inline-start: $global-margin / 3;
    color: $lb-color-alert;
    font-size: $lb-side-nav-font-size;
  }

  &__count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: rem-calc(20px);
    height: rem-calc(20px);
    padding: 0 rem-calc(6px);
    border-radius: 999px;
    background-color: $lb-color-alert;
    color: $lb-white;
    font-size: rem-calc(11px);
    font-weight: 700;
    line-height: 1;
    margin-inline-start: $global-margin / 2;
  }

  &:hover,
  &:focus,
  &--active {
    color: $lb-side-nav-active-color;

    path {
      fill: $lb-side-nav-active-color;
    }
  }

  &--disabled,
  &--disabled:hover,
  &--disabled:focus {
    cursor: default;
    color: $lb-side-nav-disabled-color;

    path {
      fill: $lb-side-nav-disabled-color;
    }
  }
}

.off-canvas--collapsed {
  .navigation-item-link__arrow-icon,
  .navigation-item-link__title {
    display: none;
  }
}
</style>
